import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarDaysIcon,
  TagIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

/**
 * "Why book with Tafwija" — three brand promises pulled from the legacy
 * OurValues block, redesigned. Pure static content, no API.
 */
const VALUES = [
  {
    icon: CalendarDaysIcon,
    titleKey: 'home.valuesCancelTitle',
    bodyKey: 'home.valuesCancelBody',
    tint: 'bg-brand-50 text-brand-500 ring-brand-100',
    accent: 'before:bg-brand-500',
  },
  {
    icon: TagIcon,
    titleKey: 'home.valuesPriceTitle',
    bodyKey: 'home.valuesPriceBody',
    tint: 'bg-accent-500/15 text-accent-700 ring-accent-500/30',
    accent: 'before:bg-accent-500',
  },
  {
    icon: ChatBubbleLeftRightIcon,
    titleKey: 'home.valuesSupportTitle',
    bodyKey: 'home.valuesSupportBody',
    tint: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    accent: 'before:bg-emerald-500',
  },
];

function ValueCard({ value, t }) {
  const Icon = value.icon;
  return (
    <div
      className={`group relative flex flex-col items-start gap-4 overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 shadow-card transition-all duration-300 ease-out-quint hover:-translate-y-1 hover:border-brand-100 hover:shadow-elevated before:absolute before:inset-x-0 before:top-0 before:h-1 before:origin-left before:scale-x-0 before:transition-transform before:duration-500 hover:before:scale-x-100 ${value.accent} rtl:before:origin-right`}
    >
      <span
        className={`flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ${value.tint} transition-transform duration-300 group-hover:scale-110`}
      >
        <Icon className="h-7 w-7" />
      </span>

      <h3 className="font-display text-lg font-semibold text-ink sm:text-xl">
        {t(value.titleKey)}
      </h3>
      <p className="text-sm leading-relaxed text-ink-muted">
        {t(value.bodyKey)}
      </p>
    </div>
  );
}

export default function Values() {
  const { t } = useTranslation();

  return (
    <section className="relative bg-surface-muted py-14 sm:py-20">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-500">
            {t('home.valuesEyebrow')}
          </span>
          <h2 className="mt-3 font-display text-[22px] font-semibold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            {t('home.valuesTitle')}
          </h2>
          <p className="mt-2 text-sm text-ink-muted sm:text-base">
            {t('home.valuesSubtitle')}
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:mt-14 sm:grid-cols-3 sm:gap-5">
          {VALUES.map((v) => (
            <ValueCard key={v.titleKey} value={v} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}
